import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import api from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // iter281 · Über die `api`-Instanz (mit 401→/auth/refresh-Interceptor), damit
      // ein abgelaufener Access-Token bei aktiver Session still erneuert wird, statt
      // den User sofort auszuloggen. Impersonation-Bearer wird vom api-Request-
      // Interceptor automatisch gesetzt.
      const { data } = await api.get(`/auth/me`);
      // Normalize: backend returns `id`, frontend code expects `_id` as well
      setUser({ ...data, _id: data._id || data.id });
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  /**
   * CRITICAL: Jeder reguläre Auth-Flow MUSS den alten Impersonation-Token
   * aus sessionStorage entfernen. Sonst sendet der api.js-Interceptor bei
   * jedem Request den alten Bearer-Header, den das Backend über Cookies
   * priorisiert → User landet fälschlicherweise als impersonierter User.
   *
   * Bug-Report Owner 2026-02: „Ich habe mich mit Frank eingeloggt und bin
   * beim User test_iter39_480835_bob gelandet" – wurde durch stale
   * sessionStorage.st_impersonate_token verursacht.
   */
  const _clearImpersonation = () => {
    try {
      sessionStorage.removeItem("st_impersonate_token");
      sessionStorage.removeItem("st_impersonated_user_id");
      sessionStorage.removeItem("st_impersonated_email");
    } catch { /* ignore */ }
  };

  const login = async (email, password) => {
    _clearImpersonation();
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
    // Sub-Batch 2FA-1: If 2FA enabled, server returns { require_2fa, challenge_token } without cookies
    if (data && data.require_2fa) {
      return data;
    }
    setUser({ ...data, _id: data._id || data.id });
    return data;
  };

  const verify2faLogin = async (challenge_token, code) => {
    _clearImpersonation();
    const { data } = await axios.post(
      `${API}/api/auth/2fa/verify-login`,
      { challenge_token, code },
      { withCredentials: true },
    );
    setUser({ ...data, _id: data._id || data.id });
    return data;
  };

  const register = async (email, password, newsletterOptIn = false) => {
    _clearImpersonation();
    const { data } = await axios.post(`${API}/api/auth/register`, { email, password, newsletter_opt_in: !!newsletterOptIn }, { withCredentials: true });
    setUser({ ...data, _id: data._id || data.id });
    return data;
  };

  const logout = async () => {
    _clearImpersonation();
    await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, ready: !loading, login, verify2faLogin, register, logout, refreshUser }}>
      {loading ? (
        <div data-testid="auth-bootstrap" className="min-h-screen bg-white flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
        </div>
      ) : children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
